import api from '@/factories/api';
import EventBus from '@/factories/eventBus';
import { ablyAction } from '@/lib/ably';
import router from '@/router';
import clone from 'lodash/clone';
import throttle from 'lodash/throttle';
import { FEATURES } from '@/access/features';

// Throttled so the count can ride the threads refresh (which is how it notices a
// NEW after-hours message arriving while a rep is sitting on the screen) without
// costing a request every time that refresh fires. trailing:true so the last call
// in a burst still lands -- otherwise a close/reopen can leave the badge stale.
// See #5814.
const AFTER_HOURS_COUNT_INTERVAL = 5000;

const fetchAfterHoursCount = throttle(
  async (commit) => {
    try {
      // Team-scoped only -- api() adds the team. threadsFilters is deliberately
      // NOT passed: it carries `search`, and the badge must mean "overnight
      // conversations waiting", not "...that match what I just typed".
      const params = { auto_closed: true, threads: true };
      const threads = await api().get('messages/conversations', { params });
      commit('setAfterHoursCount', (threads || []).length);
    } catch (err) {
      // Deliberately leave the previous count alone. Committing 0 here would tell
      // a rep "nothing waiting" because a request failed -- the exact thing this
      // badge exists to prevent. A stale number beats a false all-clear.
    }
  },
  AFTER_HOURS_COUNT_INTERVAL,
  { leading: true, trailing: true }
);

export default {
  async loadMessages({ commit }) {
    try {
      const messages = await api().get('messages');
      commit('setMessages', messages);
    } catch (err) {
      throw err;
    } finally {
      commit('setMessagesLoaded', true);
    }
  },

  async loadSingleMessage({ commit }, messageId) {
    commit('setMessageLoading', true);
    try {
      let message = await api().get(`messages/${messageId}`);
      commit('updateMessageInList', message);
    } catch (error) {
      throw error;
    } finally {
      commit('setMessageLoading', false);
    }
  },

  async loadMessageFeed({ commit, getters }, { messageId, event }) {
    commit('setMessageLoading', true);
    try {
      const path = `messages/events?message_id=${messageId}&event=${event}`;
      const messageFeed = await api().get(path);
      const message = getters.singleMessage(messageId);
      const updatedMessage = { ...message, feed: messageFeed?.results };
      commit('updateMessageInList', updatedMessage);
    } catch (error) {
      throw error;
    } finally {
      commit('setMessageLoading', false);
    }
  },

  async loadThreads(
    { commit, getters, dispatch, rootGetters, rootState },
    { filters, fromPortal = false, interactive = false }
  ) {
    commit('setThreadsLoading', true);
    if (interactive) {
      commit('setThreadsFiltering', true);
    }
    try {
      let path = 'messages/conversations';
      let params = null;
      if (fromPortal) {
        path = 'cg/conversations';
      } else {
        commit('setThreadsFilters', filters);
        params = getters.threadsFilters;
      }

      const threads = await api().get(path, { params });
      commit('setThreads', threads);

      // Riding the threads refresh is what lets the badge notice a new
      // after-hours message arriving while the rep has the screen open. The
      // throttle above keeps that from costing a request per refresh.
      if (!fromPortal) {
        dispatch('loadAfterHoursCount');
      }

      if (
        !fromPortal &&
        rootGetters['users/isFeatureEnabled'](FEATURES.CUSTOM_AGENTS)
      ) {
        // Contacts already in the store stay current via agent.status events
        // and the rehydrate on reconnect / tab focus.
        const hydrated = rootState.agentPresence.hydrated;
        const contactIds = (threads || [])
          .filter((t) => t.type === 'contact')
          .map((t) => t.type_id)
          .filter((id) => id && !(id in hydrated));
        if (contactIds.length) {
          dispatch('agentPresence/hydrateContacts', contactIds, { root: true });
        }
      }
    } finally {
      commit('setThreadsLoading', false);
      if (interactive) {
        commit('setThreadsFiltering', false);
      }
    }
  },

  async searchThreads({ commit, getters }, params) {
    return await api().get('messages/conversations', { params });
  },

  // Counts the threads the after-hours auto-reply closed, so reps see on
  // returning to work that overnight messages are waiting. Deliberately does NOT
  // commit setThreadsFilters -- the `closed: true` here is for this count only
  // and must not leak into the list the user is actually looking at. See #5814.
  // ponytail: counts client-side off the existing threads endpoint, which caps at
  // 200 rows -- so the badge undercounts past 200 after-hours threads. A dedicated
  // count endpoint is the fix if any org ever gets near that in one night.
  async loadAfterHoursCount({ commit }) {
    fetchAfterHoursCount(commit);
  },

  trackMessage({ commit, getters }, data) {
    api().post('cg/track_message', data);
    let message = getters.conversation.find(
      ({ pid }) => pid === data.message_pid
    );
    if (message && !message.open_count) {
      commit('updateConversation', {
        ...message,
        open_count: 1,
      });
    }
  },

  refreshPortalThreads({ dispatch, getters }, newMessage) {
    // if new message belongs to an open thread - ignore
    if (newMessage) {
      const route = router.currentRoute || {};
      if (newMessage?.thread_id === route?.params?.threadId) {
        return;
      }
    }

    dispatch('loadThreads', {
      fromPortal: true,
    });
  },

  async getRecipientsForPortal({ commit, getters }) {
    if (getters.portalRecipientsLoaded) return;
    try {
      const recipients = await api().get('cg/recipients');
      commit('setPortalRecipients', recipients);
    } catch (err) {
      throw err;
    }
  },

  async changeThread({ commit, getters, dispatch }, data) {
    try {
      await api().put(`messages/${data.id}`, data);
      const originalThread = clone(getters.singleThread(data.id));

      if (data.closed) {
        let threads = getters.threads.filter((i) => i.id !== data.id);
        commit('setThreads', threads);
      }

      ablyAction('publish', Object.assign(data, { event: 'threadToggled' }));

      EventBus.$emit('reloadThreads');
      EventBus.$emit('threadToggled', data);

      // unassign users
      if (data.closed && originalThread?.assigned_to.length) {
        dispatch('assignUsersToThread', {
          thread_id: data.id,
          users: [],
        });
      }

      // Refresh here too: the threads refresh alone can leave the badge a beat
      // behind after the rep closes or reopens something. The throttle collapses
      // this with whatever the refresh fires. See #5814.
      dispatch('loadAfterHoursCount');
    } catch (err) {
      throw err;
    }
  },

  async loadConversation({ commit }, data) {
    const { filters, fromPortal = false, reload = false } = data;

    if (!reload) {
      commit('setConversationLoading', true);
    }

    try {
      let basePath = fromPortal ? 'cg' : 'messages';
      let params = filters;
      let path = `${basePath}/conversations`;
      const conversation = await api().get(path, { params });
      commit('setConversation', conversation);
      return conversation;
    } catch (err) {
      throw err;
    } finally {
      commit('setConversationLoading', false);
    }
  },

  // Keep dialog history out of the shared conversation state.
  async loadBookmarkFolderMessages({}, { groupId, folderId }) {
    return api().get('messages/conversations', {
      params: {
        type: 'bookmark',
        type_id: groupId,
        lesson_folder_id: folderId,
      },
    });
  },

  async assignUsersToThread({}, data) {
    try {
      await api().post('messages/assignments', data);
      ablyAction('publish', Object.assign(data, { event: 'threadAssigned' }));
    } catch (err) {
      throw err;
    }
  },

  handleThreadUpdates({ commit, getters, dispatch }, data = {}) {
    if (data?.clientId === getters['users/currentUser']?.id) return;

    const conversationsRoutes = ['conversations-chat', 'conversations'];
    if (!conversationsRoutes.includes(router.currentRoute?.name)) return;

    const isThreadExists = getters.threads.some(({ thread_id }) => {
      return thread_id === data.thread_id;
    });

    if (isThreadExists) {
      commit('updateThread', {
        thread_id: data.thread_id,
        data: {
          assigned_to: data.users || [],
        },
      });
      return;
    }

    if (getters.threadsFilters.assigned_to) {
      dispatch('loadThreads', getters.threadsFilters);
    }
  },

  async createMessage({}, message) {
    try {
      let path = 'messages';
      if (message.fromPortal) {
        path = 'cg/messages';
        delete message.fromPortal;
      }

      return await api().post(path, message);
    } catch (err) {
      throw err;
    }
  },

  async updateInternalNote({ commit }, { id, body }) {
    const note = await api().put(`messages/${id}`, { body });
    commit('updateConversation', note);
  },

  async deleteInternalNote({ commit }, id) {
    await api().delete(`messages/${id}`);
    commit('removeConversationMessage', id);
  },

  async closeMessage(_, messageId) {
    await api().put(`messages/${messageId}`, {
      closed: true,
    });

    EventBus.$emit('reloadThreads');
  },

  async updateMessage({ commit }, { update, id }) {
    await api().put(`messages/${id}`, update);
  },

  async updateMessageTags({ commit, dispatch }, { tags, message }) {
    dispatch('updateMessage', {
      update: { tags },
      id: message.id,
    });

    commit('updateConversation', {
      ...clone(message),
      tags: formatTagsForConversation(tags, message?.tags),
    });
  },

  async loadMessagesSchemas({ commit, getters }) {
    if (getters.messagesSchemasLoaded) return;

    try {
      const messageSchemas = await api().get('messages/schemas');
      commit('setMessagesSchemas', messageSchemas?.schemas);
    } catch (err) {
      throw err;
    } finally {
      commit('setMessagesSchemasLoaded');
    }
  },

  async getTwilioMediaAuth(_, url) {
    try {
      const signed = await api({ returnMessage: true }).post(
        'twilio/sign_url',
        { url }
      );
      return signed.url || url;
    } catch (error) {
      // Silently fail and return original URL if signing fails
      return url;
    }
  },
};

const formatTagsForConversation = (update, messageTags) => {
  let tags = (messageTags || []).slice();
  if (update?.remove?.length) {
    tags = tags.filter((tagId) => !update.remove.includes(tagId));
  }

  if (update?.add?.length) {
    tags.push(...update.add);
  }

  return tags;
};
